import { AlertTriangle, BarChart3, CheckCheck, Clock3, FolderKanban, PieChart, UserRound } from "lucide-react";
import {
  formatTaskDateTime,
  formatTaskMinutes,
  getTaskPriorityLabel,
  getTaskStateLabel,
  isTaskOverdue,
  taskPriorityLabels,
  taskStateOrder,
  type Task,
} from "../types/tasks";

type Props = {
  tasks: Task[];
};

export function TaskDashboardPanel({ tasks }: Props) {
  const total = tasks.length;
  const pending = tasks.filter((task) => task.estado === "pendiente").length;
  const inProgress = tasks.filter((task) => task.estado === "progreso").length;
  const inReview = tasks.filter((task) => task.estado === "revision").length;
  const done = tasks.filter((task) => task.estado === "completado").length;
  const overdue = tasks.filter(isTaskOverdue).length;
  const totalMinutes = tasks.reduce((sum, task) => sum + task.minutos_total, 0);

  const byPriority = Object.keys(taskPriorityLabels).map((priority, index) => {
    const value = tasks.filter((task) => task.prioridad === priority).length;
    const palette = ["#0b4a2f", "#00a879", "#f59e0b"];

    return {
      label: getTaskPriorityLabel(priority as "baja" | "media" | "alta"),
      value,
      percentage: total === 0 ? 0 : Math.round((value / total) * 100),
      tone: palette[index] ?? "#0b4a2f",
    };
  });

  const byProject = aggregateRows(tasks, (task) => task.proyecto_nombre ?? "Sin proyecto");
  const byResponsible = aggregateRows(tasks, (task) => task.asignado_nombre ?? "Sin asignar");

  const recentActivity = [...tasks]
    .flatMap((task) =>
      task.actividad.slice(0, 4).map((activity) => ({
        ...activity,
        taskTitle: task.titulo,
      })),
    )
    .sort((left, right) =>
      new Date(right.creado_en ?? 0).getTime() - new Date(left.creado_en ?? 0).getTime(),
    )
    .slice(0, 8);

  return (
    <div className="flex flex-col gap-5">
      <div className="grid gap-4 xl:grid-cols-4">
        <StatCard icon={FolderKanban} label="Total de tareas" value={String(total)} />
        <StatCard icon={Clock3} label="Tiempo registrado" value={formatTaskMinutes(totalMinutes)} />
        <StatCard icon={AlertTriangle} label="Vencidas" value={String(overdue)} tone="amber" />
        <StatCard icon={CheckCheck} label="Completadas" value={String(done)} />
      </div>

      <div className="grid gap-5 xl:grid-cols-[1.4fr_1fr]">
        <section className="rounded-[34px] border border-[#dfe8e4] bg-white p-5 shadow-[0_24px_70px_rgba(15,23,42,.08)]">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-2xl border border-[#00d09c]/18 bg-[#00d09c]/8 text-[#0b4a2f]">
              <BarChart3 size={18} />
            </span>
            <div>
              <h3 className="text-lg font-black text-slate-950">Estado del flujo</h3>
              <p className="text-sm font-semibold text-slate-500">
                Distribución operativa del tablero en tiempo real.
              </p>
            </div>
          </div>

          <div className="mt-5 grid gap-3 md:grid-cols-2">
            {[
              { label: "Pendientes", value: pending, state: "pendiente" },
              { label: "En progreso", value: inProgress, state: "progreso" },
              { label: "En revisión", value: inReview, state: "revision" },
              { label: "Completadas", value: done, state: "completado" },
            ].map((item) => {
              const percent = total === 0 ? 0 : Math.round((item.value / total) * 100);
              return (
                <div
                  key={item.label}
                  className="rounded-[26px] border border-[#edf1f3] bg-[#fbfdfc] p-4"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                        {item.label}
                      </div>
                      <div className="mt-1 text-2xl font-black text-slate-950">
                        {item.value}
                      </div>
                    </div>
                    <span className="rounded-full border border-[#dfe8e4] bg-white px-3 py-1 text-xs font-black text-[#0b4a2f]">
                      {percent}%
                    </span>
                  </div>

                  <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-[linear-gradient(90deg,#0b4a2f,#00d09c)]"
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="rounded-[34px] border border-[#dfe8e4] bg-white p-5 shadow-[0_24px_70px_rgba(15,23,42,.08)]">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-2xl border border-[#00d09c]/18 bg-[#00d09c]/8 text-[#0b4a2f]">
              <PieChart size={18} />
            </span>
            <div>
              <h3 className="text-lg font-black text-slate-950">Distribución por prioridad</h3>
              <p className="text-sm font-semibold text-slate-500">
                Baja, media y alta con peso porcentual y cantidad.
              </p>
            </div>
          </div>

          <div className="mt-5 grid gap-5 lg:grid-cols-[220px_minmax(0,1fr)]">
            <PriorityDonut rows={byPriority} total={total} />

            <div className="space-y-3">
              {byPriority.map((row) => (
                <PriorityLegendRow
                  key={row.label}
                  label={row.label}
                  value={row.value}
                  percentage={row.percentage}
                  tone={row.tone}
                />
              ))}
            </div>
          </div>
        </section>
      </div>

      <div className="grid gap-5 xl:grid-cols-3">
        <ListPanel title="Por proyecto" icon={FolderKanban} rows={byProject} />
        <ListPanel title="Por responsable" icon={UserRound} rows={byResponsible} />
        <ListPanel
          title="Actividad reciente"
          icon={Clock3}
          rows={recentActivity.map((item) => ({
            label: item.taskTitle,
            meta: `${item.usuario_nombre ?? "Sistema"} · ${item.accion}`,
            trailing: formatTaskDateTime(item.creado_en),
            numeric: false,
          }))}
        />
      </div>

      <section className="rounded-[34px] border border-[#dfe8e4] bg-white p-5 shadow-[0_24px_70px_rgba(15,23,42,.08)]">
        <h3 className="text-lg font-black text-slate-950">Visión rápida por estado</h3>
        <div className="mt-4 grid gap-3 md:grid-cols-4">
          {taskStateOrder.map((state) => (
            <div key={state} className="rounded-[24px] border border-[#edf1f3] bg-[#fbfdfc] p-4">
              <div className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                {getTaskStateLabel(state)}
              </div>
              <div className="mt-2 text-2xl font-black text-slate-950">
                {tasks.filter((task) => task.estado === state).length}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function aggregateRows(tasks: Task[], getter: (task: Task) => string) {
  const bucket = new Map<string, number>();

  tasks.forEach((task) => {
    const key = getter(task);
    bucket.set(key, (bucket.get(key) ?? 0) + 1);
  });

  return [...bucket.entries()]
    .map(([label, value]) => ({ label, value, numeric: true }))
    .sort((left, right) => right.value - left.value)
    .slice(0, 8);
}

function StatCard({
  icon: Icon,
  label,
  value,
  tone = "green",
}: {
  icon: typeof FolderKanban;
  label: string;
  value: string;
  tone?: "green" | "amber";
}) {
  return (
    <div className="rounded-[30px] border border-[#dfe8e4] bg-white p-5 shadow-[0_18px_44px_rgba(15,23,42,.065)]">
      <div className="flex items-center justify-between gap-3">
        <span
          className={[
            "grid h-11 w-11 place-items-center rounded-2xl border",
            tone === "amber"
              ? "border-amber-200 bg-amber-50 text-amber-700"
              : "border-[#00d09c]/18 bg-[#00d09c]/8 text-[#0b4a2f]",
          ].join(" ")}
        >
          <Icon size={18} />
        </span>
        <div className="text-right">
          <div className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">
            {label}
          </div>
          <div className="mt-1 text-2xl font-black text-slate-950">{value}</div>
        </div>
      </div>
    </div>
  );
}

function PriorityDonut({
  rows,
  total,
}: {
  rows: Array<{ label: string; value: number; percentage: number; tone: string }>;
  total: number;
}) {
  const gradient = rows
    .reduce<{ parts: string[]; offset: number }>(
      (carry, row) => {
        const nextOffset = carry.offset + row.percentage;
        carry.parts.push(`${row.tone} ${carry.offset}% ${nextOffset}%`);
        return { parts: carry.parts, offset: nextOffset };
      },
      { parts: [], offset: 0 },
    )
    .parts.join(", ");

  return (
    <div className="flex flex-col items-center justify-center gap-4">
      <div
        className="relative h-52 w-52 rounded-full shadow-[0_24px_60px_rgba(15,23,42,.12)]"
        style={{
          background:
            total > 0
              ? `conic-gradient(${gradient})`
              : "conic-gradient(#e2e8f0 0% 100%)",
        }}
      >
        <div className="absolute inset-[22px] grid place-items-center rounded-full border border-white/80 bg-white shadow-inner">
          <div className="text-center">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">
              Prioridades
            </div>
            <div className="mt-2 text-4xl font-black text-slate-950">{total}</div>
            <div className="mt-1 text-sm font-semibold text-slate-500">tareas</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PriorityLegendRow({
  label,
  value,
  percentage,
  tone,
}: {
  label: string;
  value: number;
  percentage: number;
  tone: string;
}) {
  return (
    <div className="rounded-[22px] border border-[#edf1f3] bg-[#fbfdfc] p-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#00d09c]/22 hover:bg-slate-50">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span
            className="h-3.5 w-3.5 rounded-full shadow-[0_0_0_4px_rgba(148,163,184,.14)]"
            style={{ backgroundColor: tone }}
          />
          <span className="text-sm font-black text-slate-900">{label}</span>
        </div>

        <span className="rounded-full border border-[#dfe8e4] bg-white px-3 py-1 text-xs font-black text-slate-700 shadow-sm">
          {value}
        </span>
      </div>

      <div className="mt-3 h-3 overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${percentage}%`, backgroundColor: tone }}
        />
      </div>

      <div className="mt-2 text-right text-xs font-bold text-slate-400">
        {percentage}%
      </div>
    </div>
  );
}

function ListPanel({
  title,
  icon: Icon,
  rows,
}: {
  title: string;
  icon: typeof FolderKanban;
  rows: Array<{
    label: string;
    value?: number;
    meta?: string;
    trailing?: string;
    numeric: boolean;
  }>;
}) {
  return (
    <section className="rounded-[34px] border border-[#dfe8e4] bg-white p-5 shadow-[0_24px_70px_rgba(15,23,42,.08)]">
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-2xl border border-[#00d09c]/18 bg-[#00d09c]/8 text-[#0b4a2f]">
          <Icon size={18} />
        </span>
        <h3 className="text-lg font-black text-slate-950">{title}</h3>
      </div>

      <div className="mt-5 space-y-3">
        {rows.length === 0 ? (
          <div className="rounded-[22px] border border-dashed border-[#dfe8e4] bg-[#fbfdfc] px-4 py-5 text-sm font-semibold text-slate-400">
            Sin datos todavía.
          </div>
        ) : (
          rows.map((row, index) => (
            <div
              key={`${row.label}-${index}`}
              className="rounded-[22px] border border-[#edf1f3] bg-[#fbfdfc] px-4 py-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-black text-slate-900">
                    {row.label}
                  </div>
                  {row.meta && (
                    <div className="mt-1 text-xs font-semibold text-slate-500">
                      {row.meta}
                    </div>
                  )}
                </div>

                <div className="shrink-0 text-right text-sm font-black text-[#0b4a2f]">
                  {row.numeric ? row.value : row.trailing}
                </div>
              </div>

              {!row.numeric && row.trailing && (
                <div className="mt-2 text-xs font-semibold text-slate-400">
                  {row.trailing}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </section>
  );
}